<script setup lang="ts">
import DialogContentLayout from "@/components/common/DialogContentLayout.vue";
import { URMRole, URMRoleKey, URMRoles } from "@/models/dto/userRoleManagement";
import { ContactGroup, Contact } from "@/models/dto/ContactGroup";
import UserRoleSelector from "@/components/UserRoleSelector.vue";
import ContactListItem from "@/components/Contact/ContactListItem.vue";
import ContactList from "@/components/Contact/ContactList.vue";
import { ref, computed, watch, getCurrentInstance, onUnmounted, nextTick } from "vue";
import {
  ContactAction,
  getContactServiceCall,
  getDisabledUserRoles,
} from "./utils/helpers";
import { useDisplay } from "@/composables/useDisplay";
import { UpdateContactRequestDTO } from "@/models/dto/contact/UpdateContactRequestDTO";
import { AddContactRequestDTO } from "@/models/dto/contact/AddContactRequestDTO";
import { ReservationDetail } from "@/models/dto";
import { formatReservationPickupDestinationText } from "@/utils/string";
import { DateTime } from "luxon";
import AlertBox from "@/components/AlertBox.vue";
import { ContactType } from "./utils/helpers";
import alert from "@/store/modules/alert";
import { isEqual } from "lodash";
import debounce from 'lodash/debounce';
import { validateEmail } from "@/utils/validators";


const defaultUserRoles = [URMRoles.TripAuthorizedUser, URMRoles.TripViewOnly];

const props = withDefaults(
  defineProps<{
    title: string;
    subtitle?: string;
    submitButtonText: string;
    isEditMode: boolean;
    contactGroups: ContactGroup[];
    contact?: Contact;
    userRoles?: URMRole[];
    defaultDisabledUserRoles?: URMRole[];
    userTypeMessage?: string;
    defaultUserType?: URMRoleKey;
    preventDefaultHeaderClose?: boolean;
    headerActionRightText?: string;
    headerActionLeftText?: string;
    tripCount?: number;
    currentTripIndex?: number;
    reservation?: ReservationDetail;
    tripName?: string;
    showTripName?: boolean;
    contactListIsEditable?: boolean;
    contactType: ContactType;
    entityId: number;
  }>(),
  {
    tripCount: 1,
    currentTripIndex: 0,
    showTripName: false,
    contactListIsEditable: false,
  }
);
const emit = defineEmits([
  "remove:contact",
  "update:contact",
  "add:contact",
  "click:header-action-left",
  "click:header-action-right",
  "input",
  "update:tripName",
  "click:edit-contact",
  "click:remove-contact",
]);

const { xs } = useDisplay();
const fullscreen = computed(() => xs.value);
const { proxy } = getCurrentInstance()!;
const t = proxy.$t.bind(proxy);

const userRoles = ref<URMRole[]>(props.userRoles || defaultUserRoles);
const email = ref("");
const tripName = ref(props.reservation?.routeName);

const submitted = ref(false);
const isSubmitting = ref(false);
const submissionError = ref("");
const valid = ref(false);


const disabledUserRoles = computed(() => {
  if (props.isEditMode && props.contact) {
    return getDisabledUserRoles(props.contact.currentUserPermissions) ?? []
  }
  return props.defaultDisabledUserRoles ?? []
})

const tripAuthorizedUser = URMRoles.TripAuthorizedUser.key;
const tripContactDisabled = computed(() =>
  disabledUserRoles.value.includes(URMRoles.TripContact)
);
const tripEditorDisabled = computed(() =>
  disabledUserRoles.value.includes(URMRoles.TripEditor)
);

const tripCountOfTotalText = computed(() => {
  return `Trip ${props.currentTripIndex + 1} of ${props.tripCount}`;
});

const pickupDropoffText = computed(() =>
  props.reservation
    ? formatReservationPickupDestinationText(props.reservation, t("common.TO"))
    : ""
);
const pickupDatetimeText = computed(() => {
  const firstStop = props.reservation?.stops?.[0];
  const pickupDatetime = firstStop?.pickupDatetime;
  const timeZone = firstStop?.address?.timeZone;
  if (!pickupDatetime || !timeZone) {
    return "";
  }
  return DateTime.fromISO(pickupDatetime, { zone: timeZone }).toFormat(
    "LL/dd/yy - hh:mm a"
  );
});

const form = ref({
  email: email.value,
  userType: props.contact?.roleKey || props.defaultUserType,
  allowModify: props.contact?.canModifyTrip ?? true,
  isTripContact: props.contact?.isTripContact ?? false,
  tripName: tripName.value,
});
// This is used to determine if the form has been modified
const initialForm = ref({ ...form.value });
const isDirty = ref(false);
watch(
  () => form.value,
  (newForm, oldForm) => {
    if (oldForm) { // Only check after initial setup
      isDirty.value = !isEqual(newForm, initialForm.value);
    }
  },
  { deep: true }
);

const vFormRef = ref();

const emailRules = [
  (v: string) => !!v || "Email is required",
  (v: string) => validateEmail(v) || "Email must be valid",
];

// Debounced validation function
const debouncedValidateEmail = debounce(() => {
  if (vFormRef.value && typeof vFormRef.value.validate === 'function') {
    vFormRef.value.validate();
  }
}, 250);

const onEmailInput = () => {
  debouncedValidateEmail();
};

const onEmailPaste = () => {
  // Validate immediately on paste
  if (vFormRef.value && typeof vFormRef.value.validate === 'function') {
    vFormRef.value.validate();
  }
};

watch(
  () => props.contact,
  (newContact) => {
    form.value = {
      email: newContact?.email || "",
      userType: newContact?.roleKey || props.defaultUserType,
      allowModify: newContact?.canModifyTrip ?? true,
      isTripContact: newContact?.isTripContact ?? false,
      tripName: props.reservation?.routeName,
    };
    initialForm.value = { ...form.value };
    if (vFormRef.value && typeof vFormRef.value.resetValidation === 'function') {
      vFormRef.value.resetValidation();
    }
  },
  { immediate: true, deep: true }
);

// Watchers for role logic
watch(
  [() => disabledUserRoles.value, () => form.value.userType],
  () => {
    if (
      !props.isEditMode &&
      disabledUserRoles.value.includes(URMRoles.TripContact)
    ) {
      form.value.isTripContact = false;
    }
    if (
      !props.isEditMode &&
      disabledUserRoles.value.includes(URMRoles.TripEditor)
    ) {
      form.value.allowModify = false;
    }
  },
  { immediate: true }
);

watch(
  [
    () => userRoles.value,
    () => props.defaultUserType,
    () => disabledUserRoles.value,
  ],
  ([roles, defaultUserType, disabledRoles]) => {
    if (form.value.userType) return;
    const hasValidDefault =
      defaultUserType &&
      roles.some((role) => role.key === defaultUserType) &&
      !disabledRoles.includes(URMRoles[defaultUserType]);
    const enabledRoles = roles.filter((role) => !disabledRoles.includes(role));
    const onlyOneEnabledRole = enabledRoles.length === 1;
    if (hasValidDefault) {
      form.value.userType = defaultUserType;
    } else if (onlyOneEnabledRole) {
      form.value.userType = enabledRoles[0].key;
    }
  },
  { immediate: true }
);

watch(
  () => props.isEditMode,
  (newValue) => {
    if (newValue && !props.contact?.canModifyTrip) {
      form.value.allowModify = false;
    }
  }
);

const onUserTypeUpdate = (newUserType) => {
  form.value.userType = newUserType;
  // When switching to Authorized User, default allowModify to true
  if (newUserType === tripAuthorizedUser) {
    form.value.allowModify = true;
  }
};

const resetForm = async () => {
  form.value = {
    email: "",
    userType: props.contact?.roleKey || props.defaultUserType,
    allowModify: props.contact?.canModifyTrip ?? true,
    isTripContact: props.contact?.isTripContact ?? false,
    tripName: props.reservation?.routeName,
  };
  submitted.value = false;
  isSubmitting.value = false;
  submissionError.value = "";

  isDirty.value = false;

  // Reset validation state
  if (vFormRef.value && typeof vFormRef.value.resetValidation === 'function') {
    vFormRef.value.resetValidation();
  }

  await nextTick();
  initialForm.value = { ...form.value };
};

const handleSubmit = async () => {
  submitted.value = true;
  submissionError.value = "";
  const isFormValid = await vFormRef.value?.validate();
  if (!isFormValid) return;

  isSubmitting.value = true;
  if (props.isEditMode) {
    handleUpdateContact();
  } else {
    handleCreateContact();
  }
};

const handleUpdateContact = async () => {
  const payload: UpdateContactRequestDTO = {
    roleKey: form.value.userType,
    isTripContact: form.value.isTripContact,
    externalUserId: props.contact?.externalId,
    canModifyTrip:
      form.value.userType === URMRoles.TripAuthorizedUser.key
        ? form.value.allowModify
        : false,
  };
  try {
    const updateContactFn = getContactServiceCall(
      props.contactType,
      ContactAction.Update
    )
    await updateContactFn(props.entityId, props.contact?.externalId!, payload);
    alert.add({
      text: "",
      color: "success",
      title: "Updated",
      dismissible: true,
    });

    emit("update:contact");
  } catch (error: any) {
    console.error(
      `Updating Contact ${props.contact?.externalId} to ${JSON.stringify(
        payload
      )} to ${props.contactType} ${
        props.entityId
      } from CreateEditContactContent failed: ${error.response.data.message}`
    );
    submissionError.value = "Updating Contact failed";
  } finally {
    isSubmitting.value = false;
    submitted.value = false;
  }
};

const handleCreateContact = async () => {
  const payload: AddContactRequestDTO = {
    isTripContact: form.value.isTripContact,
    email: form.value.email,
    roleKey: form.value.userType,
    canModifyTrip:
      form.value.userType === URMRoles.TripAuthorizedUser.key
        ? form.value.allowModify
        : false,
  };
  const addContactFn = getContactServiceCall(
    props.contactType,
    ContactAction.Add
  )
  try {
    if (form.value.email) {
      await addContactFn(props.entityId, payload);
      alert.add({
        text: "",
        color: "success",
        title: "Added",
        dismissible: true,
      });
      emit("add:contact");
      await resetForm();
    }
  } catch (error: any) {
    console.error(
      `Adding New Contact ${JSON.stringify(payload)} to ${props.contactType} ${
        props.entityId
      } from CreateEditContactContent failed: ${error.response.data.message}`
    );
    submissionError.value = "Adding New Contact failed";
  } finally {
    isSubmitting.value = false;
    submitted.value = false;
  }
};

watch(
  () => form.value.tripName,
  (val) => {
    emit("update:tripName", val);
  }
);

const handleEditContactClick = (contact: Contact) => {
  emit("click:edit-contact", contact);
};

const handleRemoveContactClick = (contact: Contact) => {
  emit("click:remove-contact", contact);
};

onUnmounted(() => {
  debouncedValidateEmail.cancel();
});
</script>



<template>
  <DialogContentLayout
    id="create-edit-contact-content"
    :title="title"
    :subtitle="subtitle"
    :header-action-right-text="headerActionRightText"
    @click:header-action-right="emit('click:header-action-right')"
    :header-action-left-text="headerActionLeftText"
    @click:header-action-left="emit('click:header-action-left')"
    :prevent-default-header-close="preventDefaultHeaderClose"
    @close="emit('input', false)"
  >
    <div class="d-flex flex-column flex-grow-1">
      <v-form ref="vFormRef" v-model="valid" @submit.prevent>
        <div v-if="showTripName" class="d-flex flex-column margin-t-2">
          <div
            v-if="tripCount > 1"
            class="d-flex flex-row align-center justify-space-between margin-b-2"
          >
          <div class="d-flex flex-column">
            <span class="font-bold margin-r-1">
              {{ tripCountOfTotalText }}
            </span>
            <span class="font-12">{{ form.tripName }}</span>
          </div>
            <div class="reservation-info">
              <span class="font-bold text-gray-light" v-if="reservation">{{
                pickupDropoffText
              }}</span>
              <span class="margin-t-0 font-12" v-if="reservation">
                {{ $t("common.PICKUP") }} {{ pickupDatetimeText }}
              </span>
            </div>
          </div>
        </div>
        <AlertBox
          :active="submissionError.length > 0"
          type="error"
          class="margin-b-4"
        >
          {{ submissionError }}
        </AlertBox>

        <div class="d-flex flex-column">
          <div v-if="!isEditMode">
            <label class="font-12">{{ $t("common.EMAIL") }}*</label>
            <v-text-field
              id="create-edit-contact-email-input"
              placeholder="Enter email address"
              hide-details="auto"
              v-model="form.email"
              :rules="emailRules"
              required
              solo
              flat
              outlined
              class="border-radius-8 margin-b-2"
              validate-on-blur
              @input="onEmailInput"
              @paste="onEmailPaste"
            />
          </div>
          <div v-if="isEditMode && contact">
            <h3 class="font-14 font-bold margin-b-2">
              {{ $t("tripContact.CONTACT_NAME") }}
            </h3>
            <ContactListItem
              :contact="contact"
              :id="`contact-${contact.externalId}`"
              :align-left="true"
              :editable="false"
            />
          </div>
          <UserRoleSelector
            :model-value="form.userType"
            :user-roles="userRoles"
            :disabled-user-roles="disabledUserRoles"
            :message="userTypeMessage"
            @update:model-value="onUserTypeUpdate"
          />
        </div>

        <div class="d-flex flex-column margin-b-3">
          <template v-if="form.userType === tripAuthorizedUser">
            <v-checkbox
              id="create-edit-contact-allow-modify-checkbox"
              v-model="form.allowModify"
              hide-details="auto"
              :disabled="tripEditorDisabled"
            >
              <template #label>
                <span class="text-gray">{{
                  $t("tripContact.AUTH_USER_DESC")
                }}</span>
              </template>
            </v-checkbox>
          </template>
          <v-divider class="margin-t-3 margin-b-2" />
          <v-checkbox
            id="create-edit-contact-trip-contact-checkbox"
            v-model="form.isTripContact"
            hide-details="auto"
            :disabled="tripContactDisabled"
            class="align-self-start"
          >
            <template #label>
              <div class="d-flex flex-column">
                <div class="d-flex flex-row align-center">
                  {{ $t("tripContact.SET_TRIP_CONTACT") }}
                  <span class="font-12 margin-l-1"
                    >({{ $t("tripContact.BACKUP_CONTACT") }})</span
                  >
                </div>
                <span class="font-12">
                  {{ $t("tripContact.BACKUP_CONTACT_DESC") }}</span
                >
              </div>
            </template>
          </v-checkbox>
        </div>
      </v-form>
      <h4
        v-if="contactGroups.length > 0 && !isEditMode"
        class="font-book font-14 margin-t-1 margin-b-n2"
      >
        {{ $t("users.USERS_WITH_ACCESS") }}
      </h4>
      <ContactList
        v-if="!isEditMode"
        :contact-groups="contactGroups"
        :hide-edit="!contactListIsEditable"
        @click:edit-contact="handleEditContactClick"
        @click:delete-contact="handleRemoveContactClick"
      />
    </div>

    <template #footer>
      <div class="d-flex flex-column">
        <v-btn
          v-if="isEditMode && fullscreen"
          color="red"
          class="border-radius-12 margin-t-4 flex-grow-1 border-1"
          :small="fullscreen"
          outlined
          @click="handleRemoveContactClick(contact)"
          :disabled="isSubmitting"
          :loading="isSubmitting"
        >
          {{ $t("tripContact.REMOVE_CONTACT") }}
        </v-btn>
        <v-btn
          id="create-edit-contact-submit-button"
          color="primary"
          class="border-radius-12 margin-t-4 flex-grow-1"
          :small="fullscreen"
          @click="handleSubmit"
          :disabled="isSubmitting || !valid || !isDirty"
          :loading="isSubmitting"
        >
          {{ submitButtonText }}
        </v-btn>
      </div>
    </template>
  </DialogContentLayout>
</template>

<style lang="scss" scoped>
@import "@/scss/colors";

::v-deep .v-text-field {
  fieldset {
    border: 1px solid $midnight-gray-100 !important;
  }
}
::v-deep > .v-input__control > .v-input__slot {
  align-items: flex-start;
}

.reservation-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  text-align: end;
}
</style>
